export type KindredShareType = "activity" | "channel" | "event" | "feed_post" | "room";
export type KindredShareAccessScope = "auth" | "members" | "public";

export interface KindredSharePayload {
  version: 1;
  type: KindredShareType;
  targetId: string;
  title: string;
  subtitle?: string | null;
  imageUrl?: string | null;
  route: string;
  externalUrl: string;
  accessScope: KindredShareAccessScope;
}

const OPEN_MARKER = "[[kindred-share]]";
const CLOSE_MARKER = "[[/kindred-share]]";
const KINDRED_WEB_ORIGIN = "https://www.kindredcommunity.app";

export const buildKindredWebUrl = (path: string) =>
  `${KINDRED_WEB_ORIGIN}${path.startsWith("/") ? path : `/${path}`}`;

export const encodeKindredShareContent = (note: string, payload: KindredSharePayload) => {
  const trimmedNote = note.trim();
  return [OPEN_MARKER, JSON.stringify(payload), CLOSE_MARKER, trimmedNote]
    .filter(Boolean)
    .join("\n");
};

export const composeKindredContent = (
  note: string,
  payload?: KindredSharePayload | null,
) => {
  const trimmedNote = note.trim();
  return payload ? encodeKindredShareContent(trimmedNote, payload) : trimmedNote;
};

export const parseKindredShareContent = (content: string | null | undefined) => {
  if (!content) {
    return { payload: null, text: "" };
  }

  const trimmed = content.trim();
  if (!trimmed.startsWith(OPEN_MARKER)) {
    return { payload: null, text: content };
  }

  const closeIndex = trimmed.indexOf(CLOSE_MARKER);
  if (closeIndex === -1) {
    return { payload: null, text: content };
  }

  const payloadText = trimmed.slice(OPEN_MARKER.length, closeIndex).trim();
  const trailing = trimmed.slice(closeIndex + CLOSE_MARKER.length).trim();

  try {
    const payload = JSON.parse(payloadText) as KindredSharePayload;
    if (payload?.version !== 1 || !payload.type || !payload.route || !payload.targetId || !payload.title) {
      return { payload: null, text: content };
    }

    return {
      payload,
      text: trailing,
    };
  } catch {
    return { payload: null, text: content };
  }
};

export const getKindredShareTypeLabel = (type: KindredShareType) => {
  switch (type) {
    case "activity":
      return "Activity";
    case "channel":
      return "Channel";
    case "event":
      return "What's On";
    case "feed_post":
      return "Kinfeed post";
    case "room":
      return "Chat Room";
    default:
      return "Kindred";
  }
};

export const getKindredSharePreview = (content: string | null | undefined) => {
  const parsed = parseKindredShareContent(content);
  if (!parsed.payload) {
    return (content ?? "").trim();
  }

  const prefix = getKindredShareTypeLabel(parsed.payload.type);
  if (parsed.text) {
    return `${parsed.text} · ${prefix}: ${parsed.payload.title}`;
  }

  return `${prefix}: ${parsed.payload.title}`;
};

export const buildKindredExternalShareText = (payload: KindredSharePayload) =>
  [payload.title, payload.subtitle, payload.externalUrl].filter(Boolean).join("\n");
